import React, { useState } from 'react';
import { htmlToText } from './RunReminders';
import { invitesStore } from './store/invitesStore';
import Icon from './Icon';
import './MessageAgents.css';

const API = '';   // same-origin (dev proxies /api → :3001)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const escHtml = (s) => String(s).replace(/[<>&]/g, (c) => (c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;'));

// Parse a free-form list of emails separated by comma, semicolon, colon,
// whitespace, or newlines. Returns { valid, invalid } (both de-duplicated).
function parseEmails(raw) {
  const parts = String(raw || '').split(/[,;:\s]+/).map(s => s.trim()).filter(Boolean);
  const seen = new Set();
  const valid = [], invalid = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    (EMAIL_RE.test(p) ? valid : invalid).push(p);
  }
  return { valid, invalid };
}

// System-level "Invite" tab: paste a list of prospect emails and send them a
// hard-coded boilerplate invitation with a deep-link to the sign-up page.
function InviteAgents({ trialDays = 30, mlsId = null, mlsName = '', orgId = null, orgName = '' }) {
  const [raw, setRaw]         = useState('');
  const [status, setStatus]   = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);   // [{ email, status }]

  // Editable invite content (the sender can change it before sending). Each recipient's
  // personalized single-use sign-up link is appended automatically.
  const defaultSubject = orgId
    ? `You're invited to join ${orgName} on iBetterAgent`
    : `You're invited to try iBetterAgent — ${trialDays}-day free trial`;
  const defaultMessage = orgId
    ? `Hi there,\n\nYou've been invited to join ${orgName} on iBetterAgent — the relationship & reminder platform built for real-estate agents who stay in business by staying in touch.\n\nAutomatically send birthday, sale-anniversary, and holiday reminders to your clients, import your contacts and closed deals, and never let a relationship go cold.\n\nJust click the link below to create your account — your email is already filled in.`
    : `Hi there,\n\nYou're invited to try iBetterAgent — the relationship & reminder platform built for real-estate agents who stay in business by staying in touch.\n\nAutomatically send birthday, sale-anniversary, and holiday reminders to your clients, import your contacts and closed deals, and never let a relationship go cold.\n\nStart your free ${trialDays}-day trial — no credit card required. Just click the link below to create your account${mlsId ? ` — your MLS (${mlsName}) is already set` : ''}.`;
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);

  const { valid, invalid } = parseEmails(raw);

  // The signup link pins the scope and pre-fills the recruit's email — all arrive
  // locked on the sign-up form since we already know them. Org invites join the org
  // directly (the org already belongs to an MLS); MLS invites pin the MLS; the
  // system invite lets the recruit pick their MLS.
  // Each link carries a single-use token (minted per recipient on send). The token
  // encodes the scope (org/MLS) + email server-side, so the link works exactly once.
  function linkFor(token) {
    const params = new URLSearchParams({ signup: '1' });
    if (token) params.set('invite', token);
    return `${window.location.origin}/?${params.toString()}`;
  }
  // The sender's message, then the personalized sign-up link (always appended).
  function htmlFor(link) {
    const body = escHtml(message).trim().replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br>');
    return `<p>${body}</p><p><a href="${link}">${link}</a></p>`;
  }

  const previewHtml = htmlFor(linkFor(''));   // generic link for the on-screen preview

  async function send() {
    setStatus('');
    if (!valid.length) { setStatus('⚠ Enter at least one valid email address.'); return; }
    setSending(true); setStatus('Sending…');
    // Mint one single-use invite token per recipient.
    let byEmail;
    try {
      const { invites = [] } = await invitesStore.mint({ orgId, mlsId, emails: valid });
      byEmail = new Map(invites.map(i => [i.email, i.token]));
    } catch {
      setSending(false); setStatus('⚠ Could not create invitations. Please try again.');
      return;
    }
    const out = [];
    for (const email of valid) {
      const token = byEmail.get(email);
      if (!token) { out.push({ email, status: 'failed' }); continue; }
      // Each recipient gets a link carrying their own single-use token.
      const html = htmlFor(linkFor(token));
      const text = htmlToText(html);
      try {
        const res = await fetch(`${API}/api/send-reminder`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: email, subject, html, text }),
        });
        out.push({ email, status: res.ok ? 'sent' : 'failed' });
      } catch { out.push({ email, status: 'failed' }); }
    }
    setSending(false); setStatus('');
    setResults(out);
  }

  function reset() { setResults(null); setRaw(''); setStatus(''); }

  return (
    <div className="ma-container">
      <div className="ma-header">
        <h2><Icon name="mail" size={20} /> Invite Agents</h2>
        {orgId
          ? <p>Invite agents to join <strong>{orgName}</strong>. They sign up straight into this organization — the org (and its MLS) is locked in at sign-up.</p>
          : <p>Send prospective agents a ready-made invitation with a link to start a free {trialDays}-day trial.
              {mlsId && <> Recruits invited here join <strong>{mlsName}</strong> — their MLS is locked in at sign-up.</>}</p>}
      </div>

      {results ? (
        <>
          {(() => {
            const sent = results.filter(r => r.status === 'sent').length;
            const failed = results.filter(r => r.status === 'failed').length;
            return (
              <div className="ma-result-summary">
                ✓ Invited {sent} {sent === 1 ? 'person' : 'people'}{failed ? ` · ${failed} failed` : ''}
              </div>
            );
          })()}
          <ul className="ma-recip-list ma-results">
            {results.map(r => (
              <li key={r.email} className="ma-recip ma-result-row">
                <span className={`ma-result-badge ma-${r.status}`}>{r.status === 'sent' ? '✓ Sent' : '✗ Failed'}</span>
                <span className="ma-recip-email">{r.email}</span>
              </li>
            ))}
          </ul>
          <div className="ma-actions">
            <button className="ma-send-btn" onClick={reset}><Icon name="mail" size={16} /> Send more invitations</button>
          </div>
        </>
      ) : (
        <>
          <label className="ma-label">Email addresses</label>
          <textarea className="ma-input invite-emails" rows={4}
            placeholder="Separate addresses with a comma, semicolon, colon, space, or new line…&#10;e.g. jane@example.com, john@example.com; sam@example.com"
            value={raw} onChange={e => { setRaw(e.target.value); setStatus(''); }} />
          <div className="invite-parsed">
            {valid.length > 0 && <span className="invite-count-ok">{valid.length} valid recipient{valid.length === 1 ? '' : 's'}</span>}
            {invalid.length > 0 && <span className="invite-count-bad">{invalid.length} skipped (invalid): {invalid.join(', ')}</span>}
          </div>

          <label className="ma-label">Subject</label>
          <input className="ma-input" value={subject} onChange={e => { setSubject(e.target.value); setStatus(''); }} />

          <label className="ma-label">Message <span className="invite-hint">(each recipient's personalized sign-up link is added automatically)</span></label>
          <textarea className="ma-input" rows={8} value={message} onChange={e => { setMessage(e.target.value); setStatus(''); }} />

          <label className="ma-label">Preview</label>
          <div className="invite-preview">
            <div className="invite-preview-subject"><strong>Subject:</strong> {subject}</div>
            <div className="invite-preview-body" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
          <p className="invite-hint">Each recipient's link sends them to sign-up with their <strong>email</strong>{orgId ? <> and organization (<strong>{orgName}</strong>)</> : mlsId ? <> and <strong>{mlsName}</strong></> : ''} already filled in.</p>

          <div className="ma-actions">
            <button className="ma-send-btn" onClick={send} disabled={sending || valid.length === 0}>
              {sending ? 'Sending…' : <><Icon name="mail" size={16} /> {`Send ${valid.length || ''} invitation${valid.length === 1 ? '' : 's'}`.trim()}</>}
            </button>
            {status && <span className="ma-status">{status}</span>}
          </div>
        </>
      )}
    </div>
  );
}

export default InviteAgents;
